/**
 * Add items as a spreadsheet-like grid (Catalog idea 7): paste every column from Excel at once (Part,
 * Description, Brand, Category, Unit, Min, Lead, Supplier — a header row is recognised), see which lines
 * are already in the catalog or repeated in the paste before saving, then save the clean lines in one
 * batch.
 */
import { useEffect, useMemo, useState } from 'react';
import { ClipboardPaste, Plus, TriangleAlert, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { isValidPartNumber, PART_NUMBER_INVALID, parsePastedRows, partKey, plural } from './inventoryModel';

export const ITEM_COLUMNS = [
  { key: 'part_number', label: 'Part no. *', width: 'w-[150px]', mono: true },
  { key: 'description', label: 'Description', width: 'min-w-[220px]' },
  { key: 'brand', label: 'Brand', width: 'w-[110px]' },
  { key: 'category', label: 'Category', width: 'w-[120px]' },
  { key: 'unit', label: 'Unit', width: 'w-[64px]' },
  { key: 'min_stock_level', label: 'Min', width: 'w-[64px]', numeric: true },
  { key: 'lead_time_days', label: 'Lead (d)', width: 'w-[72px]', numeric: true },
  { key: 'preferred_supplier', label: 'Supplier', width: 'w-[130px]' }
];

const ALIASES = {
  part_number: ['part', 'model', 'item code', 'code', 'p/n', 'pn'],
  description: ['description', 'desc', 'name'],
  brand: ['brand', 'make', 'manufacturer'],
  category: ['category', 'group', 'type'],
  unit: ['unit', 'uom'],
  min_stock_level: ['min'],
  lead_time_days: ['lead'],
  preferred_supplier: ['supplier', 'vendor']
};

const blankRow = () => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  part_number: '', description: '', brand: '', category: '', unit: 'pcs', min_stock_level: '', lead_time_days: '', preferred_supplier: ''
});

const isBlank = (row) => ITEM_COLUMNS.every((column) => column.key === 'unit' || !String(row[column.key] ?? '').trim());

/**
 * Each row's state: 'ok' | 'empty' | 'missing-part' | 'bad-part' | 'exists' | 'repeat' | 'bad-number'.
 * 'bad-part' (BUG-003): a slip such as '12' or 'x' that the server refuses (isValidPartNumber is its rule) —
 * flagged here so it is held back like the other flagged lines instead of failing the whole batch on Save.
 */
export function classifyRows(rows, existingKeys) {
  const seen = new Map();
  return rows.map((row) => {
    if (isBlank(row)) return 'empty';
    const key = partKey(row.part_number);
    if (!String(row.part_number || '').trim() || !key) return 'missing-part';
    if (!isValidPartNumber(row.part_number)) return 'bad-part';
    if (existingKeys.has(key)) return 'exists';
    if (seen.has(key)) return 'repeat';
    seen.set(key, true);
    const numbers = [row.min_stock_level, row.lead_time_days].filter((value) => String(value ?? '').trim() !== '');
    if (numbers.some((value) => !Number.isInteger(Number(value)) || Number(value) < 0)) return 'bad-number';
    return 'ok';
  });
}

const STATE_TEXT = {
  exists: 'Already in catalog',
  repeat: 'Repeated above',
  'missing-part': 'Part no. missing',
  'bad-part': 'Not a real part no.',
  'bad-number': 'Min / lead must be whole numbers'
};
// The longer explanation, as the flag's tooltip (the Check column is narrow).
const STATE_HINT = { 'bad-part': PART_NUMBER_INVALID };

export default function AddItemsDialog({ open, onOpenChange, existingPartNumbers = [], onSave }) {
  const [rows, setRows] = useState([blankRow()]);
  const [paste, setPaste] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const confirm = useConfirm();

  useEffect(() => {
    if (!open) {
      setRows([blankRow()]);
      setPaste('');
      setError('');
    }
  }, [open]);

  const existingKeys = useMemo(() => new Set(existingPartNumbers.map(partKey).filter(Boolean)), [existingPartNumbers]);
  const states = classifyRows(rows, existingKeys);
  const okRows = rows.filter((_, index) => states[index] === 'ok');
  const flagged = states.filter((state) => state !== 'ok' && state !== 'empty').length;

  // Unsaved-changes guard: closing (Esc, outside click, ×) with typed or pasted lines asks first — a big
  // paste is not lost to a stray Esc. Saving closes the dialog from the page, without asking.
  const typedLines = rows.filter((row) => !isBlank(row)).length;
  const requestOpenChange = async (next) => {
    if (next || saving || (!typedLines && !paste.trim())) {
      onOpenChange?.(next);
      return;
    }
    const ok = await confirm({
      title: typedLines ? `Discard ${plural(typedLines, 'line')}?` : 'Discard the pasted text?',
      description: 'Nothing has been saved to the catalog yet.',
      tone: 'danger',
      confirmIcon: null,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing'
    });
    if (ok) onOpenChange?.(false);
  };

  const setCell = (key, field, value) => setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  const removeRow = (key) => setRows((current) => (current.length === 1 ? [blankRow()] : current.filter((row) => row.key !== key)));

  const importPaste = () => {
    const parsed = parsePastedRows(paste, ITEM_COLUMNS.map((column) => column.key), ALIASES);
    if (!parsed.length) return;
    setRows((current) => [
      ...current.filter((row) => !isBlank(row)),
      ...parsed.map((row) => ({ ...blankRow(), ...row, unit: row.unit || 'pcs' }))
    ]);
    setPaste('');
  };

  /** Pasting several cells into a grid cell spreads them over the row / the rows below (Excel-like). */
  const handleCellPaste = (event, rowIndex, columnIndex) => {
    const text = event.clipboardData?.getData('text') || '';
    if (!text.includes('\t') && !text.includes('\n')) return;
    event.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').filter((line, index, all) => line || index < all.length - 1);
    setRows((current) => {
      const next = [...current];
      lines.forEach((line, lineOffset) => {
        const target = rowIndex + lineOffset;
        if (!next[target]) next[target] = blankRow();
        const cells = line.split('\t');
        const updated = { ...next[target] };
        cells.forEach((cell, cellOffset) => {
          const column = ITEM_COLUMNS[columnIndex + cellOffset];
          if (column) updated[column.key] = cell.trim();
        });
        next[target] = updated;
      });
      return next;
    });
  };

  const removeFlagged = () => setRows((current) => {
    const kept = current.filter((_, index) => states[index] === 'ok');
    return kept.length ? kept : [blankRow()];
  });

  const submit = async (event) => {
    event.preventDefault();
    if (!okRows.length) {
      setError(flagged ? 'Every line needs fixing first — see the notes on the right.' : 'Add at least one part number');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave?.(okRows.map((row) => ({
        part_number: String(row.part_number).trim(),
        description: String(row.description || '').trim(),
        brand: String(row.brand || '').trim(),
        category: String(row.category || '').trim(),
        unit: String(row.unit || '').trim() || 'pcs',
        min_stock_level: Number(row.min_stock_level || 0),
        lead_time_days: Number(row.lead_time_days || 0),
        preferred_supplier: String(row.preferred_supplier || '').trim()
      })));
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the items'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={requestOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[min(1180px,calc(100vw-2rem))] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-ink">Add catalog items</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-3">Type in the grid or paste straight from Excel — all eight columns at once.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="catalog-form">
          <div className="rounded-xl border border-line bg-surface-2 p-3">
            <label className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-ink-2" htmlFor="catalog-paste">
              <ClipboardPaste className="h-3.5 w-3.5" aria-hidden="true" /> Paste rows from Excel
              <span className="font-normal text-ink-3">· Part, Description, Brand, Category, Unit, Min, Lead, Supplier (a header row is recognised)</span>
            </label>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <Textarea
                id="catalog-paste"
                value={paste}
                onChange={(event) => setPaste(event.target.value)}
                placeholder={'MTN6705-0008\tSpaceLogic KNX Switch/Blind Master\tSchneider\tKNX\tpcs\t2\t21\tSchneider UAE'}
                className="min-h-[72px] flex-1 font-mono text-[12px]"
              />
              <Button type="button" variant="outline" className="text-ink" onClick={importPaste} disabled={!paste.trim()} data-testid="catalog-import-paste">
                Add pasted rows
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full border-separate border-spacing-0 text-[12.5px]">
              <thead>
                <tr>
                  <th className="h-8 w-8 border-b border-line bg-surface-2 px-2 text-right text-[11px] font-bold text-ink-3">#</th>
                  {ITEM_COLUMNS.map((column) => (
                    <th key={column.key} className={cn('h-8 border-b border-line bg-surface-2 px-1.5 text-left text-[11px] font-bold text-ink-3', column.width, column.numeric && 'text-right')}>{column.label}</th>
                  ))}
                  <th className="h-8 w-[150px] border-b border-line bg-surface-2 px-2 text-left text-[11px] font-bold text-ink-3">Check</th>
                  <th className="h-8 w-9 border-b border-line bg-surface-2"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const state = states[rowIndex];
                  const flaggedRow = state !== 'ok' && state !== 'empty';
                  return (
                    <tr key={row.key} data-testid="catalog-grid-row">
                      <td className="border-b border-line-2 px-2 text-right text-[11px] tabular-nums text-ink-3">{rowIndex + 1}</td>
                      {ITEM_COLUMNS.map((column, columnIndex) => (
                        <td key={column.key} className="border-b border-line-2 p-0.5">
                          <input
                            value={row[column.key] ?? ''}
                            onChange={(event) => setCell(row.key, column.key, event.target.value)}
                            onPaste={(event) => handleCellPaste(event, rowIndex, columnIndex)}
                            inputMode={column.numeric ? 'numeric' : undefined}
                            aria-label={`${column.label.replace(' *', '')}, row ${rowIndex + 1}`}
                            className={cn(
                              'h-8 w-full min-w-0 rounded-md border border-transparent bg-transparent px-1.5 text-ink outline-none hover:border-line focus:border-[color:var(--brand-ink)] focus:bg-surface',
                              column.mono && 'font-mono',
                              column.numeric && 'text-right tabular-nums'
                            )}
                            required={rowIndex === 0 && column.key === 'part_number' && rows.length === 1}
                            data-testid={rowIndex === 0 && column.key === 'part_number' ? 'part-number-input' : undefined}
                          />
                        </td>
                      ))}
                      <td className="border-b border-line-2 px-2">
                        {flaggedRow ? (
                          <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-status-amber-ink" title={STATE_HINT[state]} data-testid="catalog-grid-flag">
                            <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />{STATE_TEXT[state]}
                          </span>
                        ) : state === 'ok' ? <span className="text-[11.5px] font-semibold text-status-green-ink">New</span> : null}
                      </td>
                      <td className="border-b border-line-2 pr-1 text-right">
                        <Button type="button" size="icon-xs" variant="ghost" className="text-ink-3" onClick={() => removeRow(row.key)} aria-label={`Remove row ${rowIndex + 1}`}>
                          <X aria-hidden="true" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" variant="outline" className="text-ink" onClick={() => setRows((current) => [...current, blankRow()])}>
              <Plus aria-hidden="true" /> Add row
            </Button>
            {flagged ? (
              <>
                <span className="text-[12.5px] text-status-amber-ink">{plural(flagged, 'line')} will be skipped</span>
                <Button type="button" size="sm" variant="ghost" className="text-ink-2" onClick={removeFlagged} data-testid="catalog-remove-flagged">Remove them</Button>
              </>
            ) : null}
            <span className="flex-1" />
            {error ? <span className="text-[12.5px] font-semibold text-status-red-ink" role="alert">{error}</span> : null}
            <Button type="submit" disabled={saving} data-testid="submit-catalog-btn">
              {saving ? 'Saving…' : okRows.length > 1 ? `Save ${okRows.length} items` : 'Save item'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
